// ANGEL FLIGHT — object pooling primitives.
//
// Zero-GC reuse for hot entities (projectiles, particles, seeds, enemies). Pure
// data structures, no Phaser — the *Phaser* objects pooled by these live in the
// systems; this layer just manages identity/recycling so node tests can verify
// the no-leak invariants.

/** A generic reuse pool. `make` mints, `reset` scrubs a freed item. */
export class ObjectPool<T> {
  private readonly free: T[] = [];
  private liveCount = 0;

  constructor(
    private readonly make: () => T,
    private readonly reset: (t: T) => void,
    prewarm = 0,
  ) {
    for (let i = 0; i < prewarm; i++) this.free.push(this.make());
  }

  /** Take an item (recycled if available, else freshly made). */
  acquire(): T {
    this.liveCount++;
    const t = this.free.pop();
    return t !== undefined ? t : this.make();
  }

  /** Return an item; it is scrubbed via `reset` and made available again. */
  release(t: T): void {
    this.reset(t);
    this.free.push(t);
    if (this.liveCount > 0) this.liveCount--;
  }

  /** Items currently parked and ready to hand out. */
  get freeCount(): number {
    return this.free.length;
  }

  /** Items currently checked out (acquired and not yet released). */
  get activeCount(): number {
    return this.liveCount;
  }

  /** Grow the free list to at least `n` parked items. */
  prewarm(n: number): void {
    while (this.free.length < n) this.free.push(this.make());
  }
}

/**
 * O(1) removal from an unordered array: swap the doomed element with the last,
 * then pop. Order is NOT preserved (fine for active-entity lists). Returns the
 * removed element, or undefined if `i` is out of range.
 */
export function swapRemove<T>(arr: T[], i: number): T | undefined {
  const n = arr.length;
  if (i < 0 || i >= n) return undefined;
  const removed = arr[i] as T; // guarded by the range check above
  const last = arr[n - 1] as T;
  arr[i] = last;
  arr.pop();
  return removed;
}

/**
 * A dense list of active items with O(1) add and swap-remove. Iterate with
 * `forEach` (back-to-front-safe — removing the current item won't skip its
 * replacement). Backed by a plain array exposed read-only via `items`.
 */
export class ActiveList<T> {
  private readonly arr: T[] = [];

  get length(): number {
    return this.arr.length;
  }

  /** Read-only view of the backing array (do not mutate directly). */
  get items(): readonly T[] {
    return this.arr;
  }

  add(t: T): void {
    this.arr.push(t);
  }

  /** Remove by index (swap-remove). Returns the removed item or undefined. */
  removeAt(i: number): T | undefined {
    return swapRemove(this.arr, i);
  }

  /** Remove the first reference-equal item. Returns true if found. */
  remove(t: T): boolean {
    const i = this.arr.indexOf(t);
    if (i < 0) return false;
    swapRemove(this.arr, i);
    return true;
  }

  /**
   * Iterate every item exactly once. Walks back-to-front so that calling
   * `removeAt(i)` inside the callback (swap-remove) does not skip the element
   * swapped into slot `i`.
   */
  forEach(fn: (t: T, i: number) => void): void {
    for (let i = this.arr.length - 1; i >= 0; i--) {
      fn(this.arr[i] as T, i);
    }
  }

  clear(): void {
    this.arr.length = 0;
  }
}
